import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { firebaseAuthBridge } from '@/lib/firebaseAuthBridge';
import { isCurrentUserModerator } from '@/services/storyRepo';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

const toAppUser = (supabaseUser) => {
  if (!supabaseUser) return null;
  const meta = supabaseUser.user_metadata || {};
  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    name: meta.full_name || meta.name || supabaseUser.email?.split('@')[0] || 'You',
    avatar_url: meta.avatar_url,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isModerator, setIsModerator] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastHydratedUserIdRef = useRef(null);

  const hydrate = useCallback(async (session) => {
    const appUser = toAppUser(session?.user);

    // Identity-stable update: only swap user object when the underlying UUID
    // actually changes. Supabase fires onAuthStateChange repeatedly (tab focus,
    // token refresh, INITIAL_SESSION + SIGNED_IN back-to-back, etc.) and
    // toAppUser() builds a fresh object each time — without this guard, every
    // event would re-render every consumer and re-fire dependent useEffects.
    setUser((prev) => {
      if (prev?.id === appUser?.id) return prev;
      return appUser;
    });

    if (appUser) {
      // Supabase session alone is enough for immediate UX (name, avatar, nav).
      // Firebase custom-token bridge + moderator probe should not block loading.
      const userChanged = lastHydratedUserIdRef.current !== appUser.id;
      if (userChanged) {
        lastHydratedUserIdRef.current = appUser.id;
        setIsModerator(false);
      }
      void (async () => {
        try {
          await firebaseAuthBridge.syncWithSupabase();
        } catch (e) {
          console.warn('[AuthContext] firebase bridge sync failed at hydrate:', e);
        }
        try {
          const mod = await isCurrentUserModerator(appUser.id);
          setIsModerator((prev) => (prev === mod ? prev : mod));
        } catch (e) {
          console.warn('[AuthContext] moderator check failed:', e);
        }
      })();
    } else {
      lastHydratedUserIdRef.current = null;
      await firebaseAuthBridge.signOutFirebase();
      setIsModerator((prev) => (prev === false ? prev : false));
    }
  }, []);

  useEffect(() => {
    let unsub;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      await hydrate(session);
      setLoading(false);

      const { data } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
        await hydrate(nextSession);
      });
      unsub = data?.subscription;
    })();

    return () => {
      try { unsub?.unsubscribe(); } catch (_) { /* noop */ }
    };
  }, [hydrate]);

  const signInWithPassword = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  /**
   * Password sign-up. With "Confirm email" disabled in Supabase Auth, this
   * returns a session immediately and onAuthStateChange logs the user in —
   * no email round-trip. We deliberately avoid magic-link / OTP sign-in so the
   * app has no dependency on Supabase's (rate-limited, paid-for-volume) email
   * sender; Google OAuth and password are the only entry points.
   */
  const signUpWithPassword = async (email, password, name) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: name ? { full_name: name } : undefined,
      },
    });
    if (error) throw error;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    await firebaseAuthBridge.signOutFirebase();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isModerator,
        signInWithPassword,
        signUpWithPassword,
        signInWithGoogle,
        signOut,
        logout: signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
